import React from "react"
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {getAltText, getPictureUri} from "../../fundamentals/CCOPicture";
import {trimTreeText} from "../../../utility/componentUtil";
import {metaDataProperty} from "../../preview/previewMetaData";
import {usePreviewContext} from "../../../context/PreviewContext";
import type {CmPictureType} from "../../types";
import type {RichTextElement} from "../../fundamentals/CCORichText/CCORichTextHelper";

interface Props {
    item: TrustElementItem | null
    previousItem?: TrustElementItem | null
    animationDuration?: number
    transitionId?: number
}

interface ContentProps {
    animationDelay?: number
    animationDuration: number
    direction: 'enter' | 'exit'
    item: TrustElementItem
}

export interface TrustElementItem {
    id: string
    teaserTitle?: string | null
    title?: string | null
    teaserText?: { textAsTree?: RichTextElement | null } | null
    picture?: CmPictureType | null
    globalFactsSettings?: {
        teaser_icon?: string | null
        teaser_icon_alt?: string | null
    } | null
}

export const DEFAULT_TRUST_ELEMENT_TRANSITION_DURATION = 2000;

const TRUST_MEDIA_WIDTH = 57;
const TRUST_MEDIA_HEIGHT = 94;
const TRUST_ICON_SIZE = 94;
const TRUST_MEDIA_WIDTH_MOBILE = 53;
const TRUST_MEDIA_HEIGHT_MOBILE = 85;
const TRUST_ICON_SIZE_MOBILE = 85;
const TRUST_MEDIA_WIDTH_MD = 50;
const TRUST_MEDIA_HEIGHT_MD = 80;
const TRUST_ICON_SIZE_MD = 80;
const TRUST_PICTURE_CROPS = [
    'portrait_ratio3x4',
    'portrait_ratio1x1',
    'landscape_ratio16x9',
    'landscape_ratio4x3',
    'landscape_ratio20x7',
];

const preferredPictureSources = new Map<string, string>();
const loadedMediaSources = new Set<string>();
const mediaLoadPromises = new Map<string, Promise<boolean>>();

const pictureSizeSx = {
    width: {
        xs: `${TRUST_MEDIA_WIDTH_MOBILE}px`,
        sm: `${TRUST_MEDIA_WIDTH_MOBILE}px`,
        md: `${TRUST_MEDIA_WIDTH_MD}px`,
        lg: `${TRUST_MEDIA_WIDTH}px`,
    },
    height: {
        xs: `${TRUST_MEDIA_HEIGHT_MOBILE}px`,
        sm: `${TRUST_MEDIA_HEIGHT_MOBILE}px`,
        md: `${TRUST_MEDIA_HEIGHT_MD}px`,
        lg: `${TRUST_MEDIA_HEIGHT}px`,
    },
};

const iconSizeSx = {
    width: {
        xs: `${TRUST_ICON_SIZE_MOBILE}px`,
        sm: `${TRUST_ICON_SIZE_MOBILE}px`,
        md: `${TRUST_ICON_SIZE_MD}px`,
        lg: `${TRUST_ICON_SIZE}px`,
    },
    height: {
        xs: `${TRUST_ICON_SIZE_MOBILE}px`,
        sm: `${TRUST_ICON_SIZE_MOBILE}px`,
        md: `${TRUST_ICON_SIZE_MD}px`,
        lg: `${TRUST_ICON_SIZE}px`,
    },
};

const contentFrameSx = {
    position: 'relative',
    width: {
        xs: '82.85vw',
        sm: '82.85vw',
        md: '462px',
        lg: '622px',
    },
    maxWidth: {
        xs: '497px',
        sm: '497px',
        md: '462px',
        lg: '622px',
    },
    height: {
        xs: '84px',
        sm: '84px',
        md: '78px',
        lg: '94px',
    },
};

export function getTrustElementMediaSource(iconPath: string) {
    if (iconPath.startsWith('/media')) {
        return iconPath;
    }
    return `/media${iconPath.startsWith('/') ? '' : '/'}${iconPath}`;
}

function getPictureKey(picture?: CmPictureType | null) {
    return picture?.id ?? picture?.uriTemplate ?? picture?.data?.uri ?? '';
}

export function getTrustElementPictureSources(picture?: CmPictureType | null, isPreview = false) {
    if (!picture) return [];

    const sources = TRUST_PICTURE_CROPS
        .map((cropName) => getPictureUri('320', isPreview, picture, cropName))
        .filter(Boolean) as string[];

    return Array.from(new Set(sources));
}

function getPreferredPictureSource(pictureKey: string, pictureSources: string[]) {
    const preferredSource = pictureKey ? preferredPictureSources.get(pictureKey) : undefined;

    if (preferredSource && pictureSources.includes(preferredSource)) {
        return preferredSource;
    }
    return undefined;
}

function isMediaSourceLoaded(src?: string) {
    return !!src && loadedMediaSources.has(src);
}

function preloadImageSource(src: string) {
    if (!src || typeof window === 'undefined') {
        return Promise.resolve(false);
    }
    if (isMediaSourceLoaded(src)) {
        return Promise.resolve(true);
    }
    const existingPromise = mediaLoadPromises.get(src);
    if (existingPromise) {
        return existingPromise;
    }

    const promise = new Promise<boolean>((resolve) => {
        const image = new window.Image();
        image.onload = () => {
            loadedMediaSources.add(src);
            resolve(true);
        };
        image.onerror = () => {
            resolve(false);
        };
        image.src = src;
    }).finally(() => {
        mediaLoadPromises.delete(src);
    });

    mediaLoadPromises.set(src, promise);
    return promise;
}

export async function preloadTrustElementMedia(item: TrustElementItem, isPreview = false) {
    const pictureKey = getPictureKey(item?.picture);
    const pictureSources = getTrustElementPictureSources(item?.picture, isPreview);
    const preferredSource = getPreferredPictureSource(pictureKey, pictureSources);

    if (preferredSource) {
        await preloadImageSource(preferredSource);
        return;
    }

    for (const pictureSource of pictureSources) {
        const loaded = await preloadImageSource(pictureSource);
        if (loaded) {
            if (pictureKey) {
                preferredPictureSources.set(pictureKey, pictureSource);
            }
            return;
        }
    }

    if (item?.globalFactsSettings?.teaser_icon) {
        await preloadImageSource(getTrustElementMediaSource(item.globalFactsSettings.teaser_icon));
    }
}

function TrustElementContent(props: ContentProps) {
    const {animationDelay = 0, animationDuration, direction, item} = props;
    const isPreview = usePreviewContext().isPreview;
    const pictureKey = getPictureKey(item?.picture);
    const pictureSources = React.useMemo(
        () => getTrustElementPictureSources(item?.picture, isPreview),
        [item?.picture, isPreview]
    );
    const iconSource = item?.globalFactsSettings?.teaser_icon
        ? getTrustElementMediaSource(item.globalFactsSettings.teaser_icon)
        : undefined;
    const [pictureSrc, setPictureSrc] = React.useState(
        () => getPreferredPictureSource(pictureKey, pictureSources)
    );
    const [iconSrc, setIconSrc] = React.useState(
        () => isMediaSourceLoaded(iconSource) ? iconSource : undefined
    );
    const [mediaResolved, setMediaResolved] = React.useState(
        () => (pictureSources.length === 0 && !iconSource)
            || !!pictureSrc
            || (pictureSources.length === 0 && !!iconSrc)
    );

    React.useEffect(() => {
        let isActive = true;
        const preferredSource = getPreferredPictureSource(pictureKey, pictureSources);
        const loadedIconSource = isMediaSourceLoaded(iconSource) ? iconSource : undefined;
        const hasMedia = pictureSources.length > 0 || !!iconSource;
        const hasResolvedIcon = pictureSources.length === 0 && !!loadedIconSource;

        setPictureSrc(preferredSource);
        setIconSrc(loadedIconSource);
        setMediaResolved(!hasMedia || !!preferredSource || hasResolvedIcon);

        if (!hasMedia || preferredSource || hasResolvedIcon) {
            return () => {
                isActive = false;
            };
        }

        preloadTrustElementMedia(item, isPreview).then(() => {
            if (!isActive) return;

            setPictureSrc(getPreferredPictureSource(pictureKey, pictureSources));
            setIconSrc(isMediaSourceLoaded(iconSource) ? iconSource : undefined);
            setMediaResolved(true);
        });

        return () => {
            isActive = false;
        };
    }, [iconSource, isPreview, item, pictureKey, pictureSources]);

    const hasPictureCandidate = pictureSources.length > 0;
    const hasPicture = !!pictureSrc;
    const hasIcon = !!iconSrc && !hasPictureCandidate;
    const hasMediaCandidate = hasPictureCandidate || !!iconSource;
    const contentVisible = !hasMediaCandidate || mediaResolved;
    const teaserText = item.teaserText?.textAsTree ? trimTreeText(item.teaserText.textAsTree) : '';
    const trustText = teaserText || item.teaserTitle || '';
    const trustTextProperty = teaserText ? 'properties.teaserText' : 'properties.teaserTitle';

    if (!trustText && !hasMediaCandidate) {
        return null;
    }

    const renderMedia = () => {
        if (hasPicture) {
            return (
                <Box
                    data-testid="trust-element-media"
                    sx={{
                        flexShrink: 0,
                        ...pictureSizeSx,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        borderRadius: '4px',
                    }}
                >
                    <Box
                        alt={getAltText(item.picture?.alt, item.picture?.title ?? item.teaserTitle ?? trustText)}
                        component="img"
                        decoding="sync"
                        loading="eager"
                        onLoad={() => {
                            if (pictureKey) {
                                preferredPictureSources.set(pictureKey, pictureSrc!);
                            }
                        }}
                        src={pictureSrc!}
                        sx={{
                            ...pictureSizeSx,
                            objectFit: 'contain',
                            objectPosition: 'center',
                            borderRadius: '4px',
                            display: 'block',
                        }}
                    />
                </Box>
            );
        }
        if (hasIcon) {
            return (
                <Box
                    data-testid="trust-element-media"
                    sx={{
                        flexShrink: 0,
                        ...iconSizeSx,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'visible',
                    }}
                >
                    <Box
                        alt={item.globalFactsSettings?.teaser_icon_alt || item.teaserTitle || ''}
                        component="img"
                        decoding="sync"
                        loading="eager"
                        src={iconSrc}
                        sx={{
                            ...iconSizeSx,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                </Box>
            );
        }
        if (hasMediaCandidate) {
            return (
                <Box
                    data-testid="trust-element-media"
                    sx={{
                        flexShrink: 0,
                        ...(hasPictureCandidate ? pictureSizeSx : iconSizeSx),
                    }}
                />
            );
        }
        return null;
    };

    const animationName = direction === 'exit' ? 'trustElementContentFadeOut' : 'trustElementContentFadeIn';
    const easing = 'cubic-bezier(0.45, 0, 0.55, 1)';

    return (
        <Box
            aria-hidden={direction === 'exit' ? true : undefined}
            sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '24px',
                visibility: contentVisible ? 'visible' : 'hidden',
                willChange: 'opacity',
                animation: contentVisible
                    ? `${animationName} ${animationDuration}ms ${easing} ${animationDelay}ms both`
                    : 'none',
                '@keyframes trustElementContentFadeIn': {
                    from: {
                        opacity: 0,
                    },
                    to: {
                        opacity: 1,
                    },
                },
                '@keyframes trustElementContentFadeOut': {
                    from: {
                        opacity: 1,
                    },
                    to: {
                        opacity: 0,
                    },
                },
                '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                    opacity: direction === 'exit' ? 0 : 1,
                },
            }}
        >
            {renderMedia()}
            <Box
                sx={{
                    flex: {
                        xs: '0 1 auto',
                        md: 1,
                    },
                    minWidth: 0,
                    width: {
                        xs: '63.2vw',
                        sm: '63.2vw',
                        md: '392px',
                        lg: 'auto',
                    },
                    maxWidth: {
                        xs: '379px',
                        sm: '379px',
                        md: '392px',
                        lg: 'none',
                    },
                }}
            >
                {trustText && (
                    <Typography
                        data-testid="trust-element-text"
                        component="p"
                        {...metaDataProperty(isPreview, trustTextProperty, item.id)}
                        sx={{
                            color: 'text.primary',
                            fontFamily: 'var(--font-family-bold)',
                            fontSize: {xs: '1.25rem', md: '22px', lg: '28px'},
                            fontWeight: 700,
                            letterSpacing: {xs: '0.01563rem', md: 0},
                            lineHeight: 1.25,
                            m: 0,
                            overflowWrap: 'break-word',
                        }}
                    >
                        {trustText}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

function TrustElementCard(props: Props) {
    const {
        item,
        previousItem,
        animationDuration = DEFAULT_TRUST_ELEMENT_TRANSITION_DURATION,
        transitionId = 0,
    } = props;

    if (!item) return null;

    const hasPreviousItem = !!previousItem && previousItem.id !== item.id;
    const exitDuration = Math.round(animationDuration * 0.55);
    const enterDelay = Math.round(animationDuration * 0.45);
    const enterDuration = animationDuration - enterDelay;

    return (
        <Box
            data-cypress="trust-element-card"
            data-testid="trust-element-card"
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
                maxWidth: {
                    xs: '100%',
                    sm: '559px',
                    md: '559px',
                    lg: '696px',
                },
                height: {
                    xs: '117px',
                    sm: '117px',
                    md: '123px',
                    lg: '160px',
                },
                backgroundColor: 'background.paper',
                border: 0,
                borderRadius: '0 0 4px 4px',
                boxShadow: '0px 2px 4px 0px #00000033',
                boxSizing: 'border-box',
                justifyContent: 'center',
                overflow: 'hidden',
                py: {
                    xs: 4,
                    sm: 4,
                    md: '22px',
                    lg: '22px',
                },
                px: 0,
            }}
        >
            <Box sx={contentFrameSx}>
                {previousItem && previousItem.id !== item.id && (
                    <TrustElementContent
                        key={`exit-${previousItem.id}-${transitionId}`}
                        animationDuration={exitDuration}
                        direction="exit"
                        item={previousItem}
                    />
                )}
                <TrustElementContent
                    key={`enter-${item.id}-${transitionId}`}
                    animationDelay={hasPreviousItem ? enterDelay : 0}
                    animationDuration={hasPreviousItem ? enterDuration : animationDuration}
                    direction="enter"
                    item={item}
                />
            </Box>
        </Box>
    );
}

export default TrustElementCard
